import { useEffect, type ReactNode } from 'react';
import { BrowserRouter, HashRouter, Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { MotionConfig } from 'framer-motion';
import { queryClient } from './lib/queryClient';
import { onUnauthenticated, storeToken } from './lib/api';
import { bridge } from './lib/desktop';
import { ToastProvider } from './components/ui/Toast';
import { ConfirmProvider } from './components/ui/Overlay';
import { RequireAuth, PublicOnly } from './components/layout/AuthGate';
import { LoginPage, SignupPage, RecoverPage, ResetPage, JoinPage } from './pages/AuthPages';
import { MailPage } from './pages/MailPage';
import { ComposePage } from './pages/ComposePage';
import { ReviewPage } from './pages/ReviewPage';
import { RemindersPage } from './pages/RemindersPage';
import { ContactsPage } from './pages/ContactsPage';
import { LibraryPage } from './pages/LibraryPage';
import { InsightsPage } from './pages/InsightsPage';
import { SettingsLayout } from './pages/settings/SettingsLayout';
import { AccountsSettings } from './pages/settings/AccountsSettings';
import { ResponderSettings } from './pages/settings/ResponderSettings';
import { AiSettings } from './pages/settings/AiSettings';
import { PreferencesSettings } from './pages/settings/PreferencesSettings';
import { SignatureSettings } from './pages/settings/SignatureSettings';
import { NotificationSettings } from './pages/settings/NotificationSettings';
import { AppearanceSettings } from './pages/settings/AppearanceSettings';
import { SecuritySettings } from './pages/settings/SecuritySettings';
import { DataSettings } from './pages/settings/DataSettings';
import { OrganizationSettings } from './pages/settings/OrganizationSettings';
import { UpdatesSettings } from './pages/settings/UpdatesSettings';
import { CategoriesSettings } from './pages/settings/CategoriesSettings';
import { NotFoundPage } from './pages/NotFoundPage';
import { ErrorBoundary } from './components/layout/ErrorBoundary';
import { LinkDevicePage } from './pages/LinkDevicePage';
import { AboutPage, PrivacyPage, TermsPage } from './pages/LegalPages';
import { ProjectPage, ProjectsPage } from './pages/ProjectsPage';
import { WelcomePage } from './pages/WelcomePage';

function UnauthListener() {
  const navigate = useNavigate();
  useEffect(
    () =>
      onUnauthenticated(() => {
        const wasSignedIn = !!queryClient.getQueryData(['me']);
        queryClient.removeQueries({ queryKey: ['me'] });
        void storeToken(null).catch(() => undefined);
        const path = window.location.hash.replace(/^#/, '') || window.location.pathname;
        if (!/^\/(login|signup|recover|reset|join)/.test(path)) navigate(`/login${wasSignedIn ? '?expired=1' : ''}`, { replace: true });
      }),
    [navigate],
  );
  return null;
}

function Router({ children }: { children: ReactNode }) {
  // Electron loads the build from file://, where only hash routing works.
  const useHash = !!bridge() || window.location.protocol === 'file:';
  return useHash ? <HashRouter>{children}</HashRouter> : <BrowserRouter>{children}</BrowserRouter>;
}

const MAIL_VIEWS = ['inbox', 'needs-reply', 'flagged', 'drafts', 'sent'] as const;

export function App() {
  return (
    <ErrorBoundary>
    <MotionConfig reducedMotion="user">
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <ConfirmProvider>
            <Router>
              <UnauthListener />
              <Routes>
                <Route element={<PublicOnly />}>
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/signup" element={<SignupPage />} />
                  <Route path="/recover" element={<RecoverPage />} />
                </Route>
                <Route path="/reset" element={<ResetPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/join/:code" element={<JoinPage />} />
                <Route element={<RequireAuth />}>
                  <Route index element={<Navigate to="/inbox" replace />} />
                  {MAIL_VIEWS.map((v) => (
                    <Route key={v} path={`/${v}`}>
                      <Route index element={<MailPage view={v} />} />
                      <Route path=":threadId" element={<MailPage view={v} />} />
                    </Route>
                  ))}
                  <Route path="/folder/:folderId">
                    <Route index element={<MailPage view="folder" />} />
                    <Route path=":threadId" element={<MailPage view="folder" />} />
                  </Route>
                  <Route path="/compose" element={<ComposePage />} />
                  <Route path="/projects" element={<ProjectsPage />} />
                  <Route path="/projects/:projectId" element={<ProjectPage />} />
                  <Route path="/projects/:projectId/:threadId" element={<ProjectPage />} />
                  <Route path="/review" element={<ReviewPage />} />
                  <Route path="/reminders" element={<RemindersPage />} />
                  <Route path="/contacts" element={<ContactsPage />} />
                  <Route path="/library" element={<LibraryPage />} />
                  <Route path="/insights" element={<InsightsPage />} />
                  <Route path="/link-device" element={<LinkDevicePage />} />
                  <Route path="/welcome" element={<WelcomePage />} />
                  <Route path="/settings" element={<SettingsLayout />}>
                    <Route index element={<Navigate to="accounts" replace />} />
                    <Route path="organization" element={<OrganizationSettings />} />
                    <Route path="accounts" element={<AccountsSettings />} />
                    <Route path="responders" element={<ResponderSettings />} />
                    <Route path="ai" element={<AiSettings />} />
                    <Route path="preferences" element={<PreferencesSettings />} />
                    <Route path="signatures" element={<SignatureSettings />} />
                    <Route path="notifications" element={<NotificationSettings />} />
                    <Route path="appearance" element={<AppearanceSettings />} />
                    <Route path="security" element={<SecuritySettings />} />
                    <Route path="data" element={<DataSettings />} />
                    <Route path="updates" element={<UpdatesSettings />} />
                    <Route path="categories" element={<CategoriesSettings />} />
                  </Route>
                  <Route path="*" element={<NotFoundPage />} />
                </Route>
              </Routes>
            </Router>
          </ConfirmProvider>
        </ToastProvider>
      </QueryClientProvider>
    </MotionConfig>
    </ErrorBoundary>
  );
}
